import type { DefaultOptions } from "@tanstack/react-query";
import { ApiError } from "@/lib/auth-api";

/** 4xx, ktore znacza „sprobuj pozniej", a nie „zle zadanie". */
const TRANSIENT_CLIENT_STATUSES = new Set([408, 429]);

/**
 * Jedno ponowienie dla bledow przejsciowych: siec i inne bledy spoza `ApiError`,
 * 5xx, 408 i 429. Pozostale 4xx sa deterministyczne — ta sama odpowiedz przyjdzie
 * drugi raz (FRONTEND-40: 403 z bramki widocznosci lecial dwa razy). Ponowienie
 * podwajalo ruch HTTP i breadcrumby oraz trzymalo zapytanie ~1 s w backoffie
 * przed pokazaniem bledu. Liczby zdarzen w Sentry nie zmienialo: `QueryCache.onError`
 * odpala raz, po wyczerpaniu ponowien. 408/429 zostaja ponawiane: backend
 * zwraca 429 przy zajetym przetwarzaniu mediow SMS i limicie IP (odrzucone
 * zadanie nie zuzywa okna), a 408 oznacza, ze zadanie nie zostalo obsluzone.
 * `failureCount` liczy wczesniejsze porazki od zera, jak `retry: 1`.
 *
 * 4xx rozpoznajemy wylacznie po `ApiError`, wiec `queryFn` na surowym `fetch`
 * rzuca `await buildApiError(res)`, a zapytanie z jednym ponowieniem ustawia
 * `retry: retryTransientErrorOnce` — lokalne `retry: 1` omija ten predykat.
 * Swiadomy wyjatek: 401 z `apiFetch` po nieudanym odswiezeniu sesji jest zwyklym
 * `Error` i jest ponawiany raz — patrz doc/UX/specyfikacja_frondend.md.
 */
export function retryTransientErrorOnce(failureCount: number, error: Error): boolean {
	if (failureCount >= 1) {
		return false;
	}
	if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
		return TRANSIENT_CLIENT_STATUSES.has(error.status);
	}
	return true;
}

/** Globalne domyslne opcje zapytan dla QueryClient aplikacji (`main.tsx`). */
export const DEFAULT_QUERY_OPTIONS = {
	// MV refresh co 1h+ → 30min staleTime jest zgodny z aktualnym SLA swiezosci.
	// Soft-navigation pomiędzy tabami bez refetchu w 30min oknie.
	// gcTime 60min — cache trzyma sie w pamięci dla nieaktywnych queries dłużej,
	// pozwala instant powrot do raportu po przejściu na inną sekcję.
	// F5 nie trafia juz backendu dla rodzin z `lib/query-persist.ts` — decyduje
	// `staleTime`, tak samo jak przy soft-navigation. ETag (`middleware/etag.rs`)
	// tego nie zastepowal: 304 oszczedza bajty, ale handler i tak wykonuje pelne
	// zapytanie, zeby policzyc hash.
	staleTime: 30 * 60_000,
	gcTime: 60 * 60_000,
	retry: retryTransientErrorOnce,
	refetchOnWindowFocus: false,
	refetchOnReconnect: true,
} satisfies DefaultOptions["queries"];
